import React, { useState, useEffect, useRef } from "react";
import { IoCheckmarkCircleSharp, IoTimeOutline } from "react-icons/io5";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firestore";
import { useUser } from "../../context/userContext";
import { motion, AnimatePresence } from "framer-motion";

const OfferwallTask = () => {
  const {
    id,
    setBalance,
    setAdsBalance,
    setAdsWatched,
    setLastAdTime,
  } = useUser();

  // UI states
  const [offerCompleted, setOfferCompleted] = useState(false);
  const [congrats, setCongrats] = useState(false);
  const [showCooldown, setShowCooldown] = useState(0);
  const [cooldownMessage, setCooldownMessage] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeTimer, setIframeTimer] = useState(0);
  const [canMarkComplete, setCanMarkComplete] = useState(false);

  // Offerwall info
  const appId = 1331;
  const apiKey = "1sqsxw8bulkrp984eb0094umxyfwnj";
  const secretKey = "df9c3e127c908e4a7dc94339b2ce8f02";
  
  const ptcToken = "x2r3a87wfn4yekgoytgqp3sidc9g8kbxm0gby2kku2ic9306bbiy49mjr4rr";

  const offerwallURL = id
    ? `https://offerwall.me/offerwall/${apiKey}/${id}?appid=${appId}&secret=${secretKey}&token=${ptcToken}`
    : "";

  const task = { cooldown: 10 * 60 * 1000 }; // 10 minutes

  const iframeCountdownRef = useRef(null);
  const cooldownCountdownRef = useRef(null);
  const preloadIframeRef = useRef(null);

  // 🔹 Real-time listener for user balance
  useEffect(() => {
    if (!id) return;
    const userRef = doc(db, "telegramUsers", id);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.balance !== undefined) setBalance(data.balance);
        if (data.adsBalance !== undefined) setAdsBalance(data.adsBalance);

        // If backend just credited → show congrats popup
        if (offerCompleted) {
          setCongrats(true);
          setTimeout(() => setCongrats(false), 3000);
          setOfferCompleted(false); // reset for next ad
        }
      }
    });

    return () => unsubscribe();
  }, [id, setBalance, setAdsBalance, offerCompleted]);

  // Preload iframe
  useEffect(() => {
    if (!offerwallURL) return undefined;
    const iframe = document.createElement("iframe");
    iframe.src = offerwallURL;
    iframe.style.display = "none";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
    preloadIframeRef.current = iframe;
    return () => {
      try {
        if (preloadIframeRef.current && preloadIframeRef.current.parentNode) {
          preloadIframeRef.current.parentNode.removeChild(preloadIframeRef.current);
        }
      } catch (e) {}
      preloadIframeRef.current = null;
    };
  }, [offerwallURL]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (iframeCountdownRef.current) clearInterval(iframeCountdownRef.current);
      if (cooldownCountdownRef.current) clearInterval(cooldownCountdownRef.current);
    };
  }, []);

  // Notifications
  const showNotification = (message) => {
    setCooldownMessage(message);
    setTimeout(() => setCooldownMessage(""), 3000);
  };

  // Start 30s timer when iframe loads
  const handleIframeLoad = () => {
    setIsLoading(false);

    if (iframeCountdownRef.current) {
      clearInterval(iframeCountdownRef.current);
      iframeCountdownRef.current = null;
    }

    setIframeTimer(90);
    setCanMarkComplete(false);
    iframeCountdownRef.current = setInterval(() => {
      setIframeTimer((prev) => {
        if (prev <= 1) {
          clearInterval(iframeCountdownRef.current);
          iframeCountdownRef.current = null;
          setCanMarkComplete(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // When user clicks Mark Complete
  const handleOfferCompletion = () => {
    if (showCooldown > 0) {
      showNotification("Please wait for the cooldown before the next ad!");
      return;
    }
    if (!canMarkComplete) {
      showNotification("Please spend at least 90s on the ad page before marking complete.");
      return;
    }

    setOfferCompleted(true);
    setLastAdTime(Date.now());
    setAdsWatched((prev) => prev + 1);

    if (cooldownCountdownRef.current) {
      clearInterval(cooldownCountdownRef.current);
      cooldownCountdownRef.current = null;
    }
    setShowCooldown(Math.floor(task.cooldown / 1000));
    cooldownCountdownRef.current = setInterval(() => {
      setShowCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownCountdownRef.current);
          cooldownCountdownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setIsPopupOpen(false);

    if (iframeCountdownRef.current) {
      clearInterval(iframeCountdownRef.current);
      iframeCountdownRef.current = null;
    }
    setIframeTimer(0);
    setCanMarkComplete(false);
  };

  return (
    <div className="mb-4 grid gap-3 w-full max-w-md mx-auto">
      <div className="border border-gray-300 rounded-xl p-5 shadow-md bg-white space-y-4 hover:shadow-lg transition duration-300">
        <h2 className="text-xl font-bold text-center text-gray-800">
          Watch Ads & Earn Rewards
        </h2>
        <p className="text-sm text-center text-gray-600">
          Your All-in-One Earning Hub 🔥 Complete simple tasks, surveys, games, PTC ads, shortlinks, and offerwall.
        </p>

        {/* Show Ad Button */}
        <div className="flex justify-center">
          <button
            onClick={() => {
              if (!id) {
                showNotification("User ID not available yet. Please open via Telegram.");
                return;
              }
              setIsLoading(true);
              setIframeTimer(0);
              setCanMarkComplete(false);
              setIsPopupOpen(true);
            }}
            disabled={showCooldown > 0 || !id}
            className={`h-10 px-6 rounded-lg text-sm ${
              showCooldown > 0 || !id
                ? "bg-gray-200 text-gray-400"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {showCooldown > 0 ? `Wait ${showCooldown}s` : "EARN SHIB"}
          </button>
        </div>

        {/* Status instead of Claim Button */}
        <div className="flex justify-center">
          {offerCompleted ? (
            <span className="text-green-600 font-medium">
              ✅ Waiting for server to credit reward...
            </span>
          ) : (
            <span className="text-gray-500 text-sm">
              Complete an offer to earn rewards
            </span>
          )}
        </div>
      </div>

      {/* Popup iframe */}
      <AnimatePresence>
        {isPopupOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50"
          >
            <div className="bg-white w-full h-full flex flex-col relative">
              <button
                onClick={() => {
                  setIsPopupOpen(false);
                  if (iframeCountdownRef.current) {
                    clearInterval(iframeCountdownRef.current);
                    iframeCountdownRef.current = null;
                  }
                  setIframeTimer(0);
                  setCanMarkComplete(false);
                }}
                aria-label="Close"
                className="absolute top-3 right-3 text-white bg-red-500 p-2 rounded-full z-50"
              >
                ✕
              </button>

              {isLoading && (
                <div className="flex justify-center items-center flex-grow">
                  <p className="text-lg text-gray-600">Loading ad...</p>
                </div>
              )}

              {offerwallURL ? (
                <iframe
                  src={offerwallURL}
                  title="Offerwall"
                  className="w-full h-full border-0"
                  onLoad={handleIframeLoad}
                />
              ) : (
                <div className="flex items-center justify-center flex-grow">
                  <p className="text-center text-gray-700">
                    No user ID yet — open inside Telegram.
                  </p>
                </div>
              )}

              <div className="p-4 flex justify-end gap-3">
                <button
                  onClick={handleOfferCompletion}
                  disabled={!canMarkComplete || showCooldown > 0}
                  className={`h-9 px-4 rounded-lg text-sm ${
                    !canMarkComplete || showCooldown > 0
                      ? "bg-gray-200 text-gray-400"
                      : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {iframeTimer > 0 ? `Wait ${iframeTimer}s` : "Mark Complete"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Congrats Notification */}
      <AnimatePresence>
        {congrats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 left-0 right-0 flex justify-center px-4 z-50"
          >
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg py-2 px-4 flex items-center gap-2 shadow">
              <IoCheckmarkCircleSharp /> Reward credited! 🎉
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Notifications */}
      <AnimatePresence>
        {cooldownMessage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 left-0 right-0 flex justify-center px-4 z-50"
          >
            <div className="bg-blue-50 border border-blue-200 text-blue-700 rounded-lg py-2 px-4 flex items-center gap-2 shadow">
              <IoTimeOutline /> {cooldownMessage}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OfferwallTask;